/* eslint-disable */
import React from "react";
import {
  Container,
  Grid,
  SimpleGrid,
  Paper,
  ThemeIcon,
  Text,
  Group,
  Button,
  Title,
  createStyles,
  Card,
  Loader,
  Stack,
} from "@mantine/core";
import {
  IconCalendar,
  IconClipboardList,
  IconUsers,
  IconBriefcase,
  IconCurrencyDollar,
  IconTrendingUp,
  IconUserPlus,
  IconChartBar,
  IconDashboard,
} from "@tabler/icons-react";
import EmployeeDepartmentStatus from "./employeeDepartment";
import { useNavigate } from "react-router-dom";
import { useGetAttendanceMonthSummary } from "api/attendanceReport/getAttendanceMonth";
import { useUser } from "hooks/useUser";
import { useGetUserPermissions } from "api/permission/getpermission";

const useStyles = createStyles((theme) => ({
  root: {},
  card: {
    height: "100%",
    borderRadius: 8,
  },
  attendanceCard: {
    height: "100%",
    borderRadius: 8,
    flex: 1, // This will make it take available space
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  smallText: {
    fontSize: 13,
    color: theme.colors.gray[6],
    marginTop: 6,
  },
  bigNumber: {
    fontSize: 13,
    fontWeight: 700,
    marginTop: 6,
  },
  deltaPositive: {
    color: theme.colors.teal[6],
    fontWeight: 700,
    marginLeft: 8,
    fontSize: 13,
    marginTop: 6,
  },
  deltaNegative: {
    color: theme.colors.red[6],
    fontWeight: 700,
    marginLeft: 8,
    fontSize: 13,
  },
  viewAll: {
    marginTop: 12,
    color: theme.colors.gray[6],
    cursor: "pointer",
    fontSize: 13,
  },
  viewBtn: { width: "100%", marginTop: theme.spacing.md },
  teamBtn: { width: "100%", marginTop: theme.spacing.md },

  rightCard: {
    height: "100%",
    borderRadius: 8,
    padding: theme.spacing.md,
    boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
  },
}));

const cardsData = [
  {
    id: 1,
    title: "Attendance",
    value: "0/0", // fallback - will be replaced by live data
    positive: true,
    icon: <IconCalendar size={18} />,
    color: "#f97316",
  },
  {
    id: 2,
    title: "Team Dashboard",
    value: "Team Overview",
    icon: <IconDashboard size={18} />,
    color: "#228be6", // Mantine blue
  },
];

export default function AdminDashboardTop({
  month,
  year,
}: {
  month?: number;
  year?: number;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const now = new Date();
  const m = month ?? now.getMonth() + 1;
  const y = year ?? now.getFullYear();

  const { data, isLoading, isError }: any = useGetAttendanceMonthSummary(m, y);
  const { user } = useUser();
  const userId = user?._id ?? "";
  const { data: permResp } = useGetUserPermissions(userId);
  const permissions = permResp?.data?.permissions ?? {};

  const totalWorkingDaysCount = data?.data?.totalWorkingDays?.count ?? 0;
  const presentCount = data?.data?.totalCurrentMonthCount?.presentCount ?? 0;

  const attendanceValue =
    totalWorkingDaysCount > 0
      ? `${presentCount}/${totalWorkingDaysCount}`
      : `${presentCount}/0`;

  const cards = cardsData.map((c) =>
    c.id === 1 ? { ...c, value: attendanceValue } : c,
  );

  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc: any, key: string) => {
      return acc && acc[key] !== undefined ? acc[key] : undefined;
    }, obj);
  };

  const hasPermission = (permissionKey: string) => {
    const value = getNested(permissions, permissionKey);

    return (
      value === true ||
      (typeof value === "object" &&
        value !== null &&
        Object.values(value).some(Boolean))
    );
  };

  const TEAMDASHBOARD_PERMISSION = "dashboardAccess.view_supervisor_dashboard";
  const showTeamDashboard = hasPermission(TEAMDASHBOARD_PERMISSION);

  return (
    <Grid>
      <Grid.Col span={4} xl={4} md={4}>
        {showTeamDashboard ? (
          // When Team Dashboard is shown - use Stack with spacing
          <Stack spacing="md" style={{ height: "100%" }}>
            <Paper className={classes.card} p="md" radius="md" shadow="sm">
              <Group position="apart">
                <Title order={3} style={{ fontSize: "18px" }}>
                  Team Dashboard
                </Title>
                <ThemeIcon
                  size={48}
                  radius={999}
                  style={{
                    backgroundColor: "#fff",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                  }}
                >
                  <div
                    className={classes.iconCircle}
                    style={{ background: "#228be6", color: "#fff" }}
                  >
                    <IconDashboard size={18} />
                  </div>
                </ThemeIcon>
              </Group>

              <div style={{ marginTop: 10 }}>
                <Button
                  variant="filled"
                  className={classes.teamBtn}
                  onClick={() => navigate("/director-dashboard")}
                  color="blue"
                >
                  View Team Dashboard
                </Button>
              </div>
            </Paper>

            {/* Attendance Card */}
            {isLoading ? (
              <Paper className={classes.card} p="md" radius="md" shadow="sm">
                <Group position="apart">
                  <Title order={3} style={{ fontSize: "18px" }}>
                    Attendance
                  </Title>
                  <ThemeIcon
                    size={48}
                    radius={999}
                    style={{
                      backgroundColor: "#fff",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      className={classes.iconCircle}
                      style={{ background: "#f97316", color: "#fff" }}
                    >
                      <IconCalendar size={18} />
                    </div>
                  </ThemeIcon>
                </Group>

                <div style={{ marginTop: 10 }}>
                  <Group position="center" align="center">
                    <Loader size="sm" />
                    <Text className={classes.smallText}>Loading...</Text>
                  </Group>
                </div>
              </Paper>
            ) : (
              cards
                .filter((c) => c.id === 1)
                .map((c) => (
                  <Paper
                    key={c.id}
                    className={classes.card}
                    p="md"
                    radius="md"
                    shadow="sm"
                  >
                    <Group position="apart">
                      <Title order={3} style={{ fontSize: "18px" }}>
                        {c.title}
                      </Title>
                      <ThemeIcon
                        size={48}
                        radius={999}
                        style={{
                          backgroundColor: "#fff",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                        }}
                      >
                        <div
                          className={classes.iconCircle}
                          style={{ background: c.color, color: "#fff" }}
                        >
                          {c.icon}
                        </div>
                      </ThemeIcon>
                    </Group>

                    <div style={{ marginTop: 10 }}>
                      <Group position="center" align="center">
                        <Text className={classes.bigNumber}>{c.value}</Text>
                      </Group>
                    </div>
                    <div style={{ marginTop: 50 }}>
                      <Button
                        variant="light"
                        className={classes.viewBtn}
                        onClick={() => navigate("/attendance-admin")}
                      >
                        View All Attendance
                      </Button>
                    </div>
                  </Paper>
                ))
            )}
          </Stack>
        ) : (
          // When only Attendance is shown - make it take full height
          <>
            {isLoading ? (
              <Paper
                className={classes.card}
                p="md"
                radius="md"
                shadow="sm"
                style={{ height: "100%" }}
              >
                <Group position="apart">
                  <Title order={3} style={{ fontSize: "18px" }}>
                    Attendance
                  </Title>
                  <ThemeIcon
                    size={48}
                    radius={999}
                    style={{
                      backgroundColor: "#fff",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      className={classes.iconCircle}
                      style={{ background: "#f97316", color: "#fff" }}
                    >
                      <IconCalendar size={18} />
                    </div>
                  </ThemeIcon>
                </Group>

                <div style={{ marginTop: 10 }}>
                  <Group position="center" align="center">
                    <Loader size="sm" />
                    <Text className={classes.smallText}>Loading...</Text>
                  </Group>
                </div>
              </Paper>
            ) : (
              cards
                .filter((c) => c.id === 1)
                .map((c) => (
                  <Paper
                    key={c.id}
                    className={classes.card}
                    p="md"
                    radius="md"
                    shadow="sm"
                    style={{ height: "100%" }}
                  >
                    <Group position="apart">
                      <Title order={3} style={{ fontSize: "18px" }}>
                        {c.title}
                      </Title>
                      <ThemeIcon
                        size={48}
                        radius={999}
                        style={{
                          backgroundColor: "#fff",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                        }}
                      >
                        <div
                          className={classes.iconCircle}
                          style={{ background: c.color, color: "#fff" }}
                        >
                          {c.icon}
                        </div>
                      </ThemeIcon>
                    </Group>

                    <div style={{ marginTop: 10 }}>
                      <Group position="center" align="center">
                        <Text className={classes.bigNumber}>{c.value}</Text>
                      </Group>
                    </div>
                    <div style={{ marginTop: 50 }}>
                      <Button
                        variant="light"
                        className={classes.viewBtn}
                        onClick={() => navigate("/attendance-admin")}
                      >
                        View All Attendance
                      </Button>
                    </div>
                  </Paper>
                ))
            )}
          </>
        )}
      </Grid.Col>

      <Grid.Col span={8} xl={8} md={8}>
        <EmployeeDepartmentStatus />
      </Grid.Col>
    </Grid>
  );
}
